/*
 * ohp-optin.js — ACQ 10% off email-capture popup behavior.
 * ------------------------------------------------------------------
 * Design spec: BrandTruth OptInPopup.jsx + optin-popup-notes.md.
 * Markup: snippets/ohp-optin-popup.liquid (rendered by layout/theme.liquid only).
 *
 * Behavior:
 *   - Quiet delay trigger (default 15s), armed once on load.
 *   - Once per browser: dismiss OR convert writes localStorage (ohp_acq_optin),
 *     and the popup never shows again for that browser.
 *   - Four gentle dismiss paths: × button, Esc key, scrim click, "No thanks".
 *   - Focus moves to the first field on open and is restored on close.
 *   - Submit fires a fire-and-forget Klaviyo client-subscribe POST, then shows
 *     the inline WELCOME10 success state with tap-to-copy.
 *   - prefers-reduced-motion disables the entrance animation.
 *
 * Config comes from data-* attributes on the [data-ohp-optin] root so the JS
 * stays static/cacheable. No dev/QA globals are exposed in production.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-ohp-optin]');
  if (!root) return;

  var STORAGE_KEY = root.getAttribute('data-storage-key') || 'ohp_acq_optin';
  var DELAY = parseInt(root.getAttribute('data-delay'), 10);
  if (isNaN(DELAY)) DELAY = 15000;
  var CODE = root.getAttribute('data-code') || 'WELCOME10';
  var COMPANY_ID = root.getAttribute('data-klaviyo-company') || '';
  var LIST_ID = root.getAttribute('data-klaviyo-list') || '';

  var dialog = root.querySelector('[data-ohp-optin-dialog]');
  var capture = root.querySelector('[data-ohp-optin-capture]');
  var success = root.querySelector('[data-ohp-optin-success]');
  var form = root.querySelector('[data-ohp-optin-form]');
  var firstField = root.querySelector('[data-ohp-optin-first-field]');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) root.setAttribute('data-reduced-motion', '');

  var lastFocus = null;
  var isOpen = false;

  function seen() {
    try { return localStorage.getItem(STORAGE_KEY); } catch (e) { return null; }
  }
  function mark(value) {
    try { localStorage.setItem(STORAGE_KEY, value); } catch (e) { /* private mode */ }
  }

  /* ---- focus trap (contained to the dialog while open) ---- */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function focusable() {
    return Array.prototype.slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }
  function onKeydown(e) {
    if (e.key === 'Escape') { close('dismissed'); return; }
    if (e.key !== 'Tab') return;
    var list = focusable();
    if (!list.length) return;
    var first = list[0];
    var last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open() {
    if (isOpen || seen()) return;
    isOpen = true;
    lastFocus = document.activeElement;
    root.hidden = false;
    document.addEventListener('keydown', onKeydown);
    var target = (success && !success.hidden) ? dialog : firstField;
    if (target && target.focus) {
      try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
    }
  }

  function close(reason) {
    if (!isOpen) return;
    isOpen = false;
    root.hidden = true;
    document.removeEventListener('keydown', onKeydown);
    if (reason) mark(reason);
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  /* ---- dismiss paths: × button, "No thanks", scrim click ---- */
  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-ohp-optin-dismiss]')) { close('dismissed'); return; }
    if (e.target === root) { close('dismissed'); return; } // scrim
  });

  /* ---- convert: submit -> Klaviyo (fire-and-forget) -> success state ---- */
  function subscribe(email, firstName) {
    if (!COMPANY_ID || !LIST_ID) {
      // No list configured yet (Welcome Series list id set in theme settings).
      // The inline code below still delivers the offer; log for the merchant.
      if (window.console) console.info('[OHP opt-in] Klaviyo list/company id not set — email not sent to Klaviyo.');
      return;
    }
    var attrs = { email: email, properties: { source: 'acq_optin_popup' } };
    if (firstName) attrs.first_name = firstName;
    try {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(COMPANY_ID), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: { profile: { data: { type: 'profile', attributes: attrs } } },
            relationships: { list: { data: { type: 'list', id: LIST_ID } } }
          }
        })
      }).catch(function () {});
    } catch (e) { /* ignore */ }
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (form.querySelector('[name="email"]') || {}).value || '';
      var firstName = (form.querySelector('[name="first_name"]') || {}).value || '';
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        var emailInput = form.querySelector('[name="email"]');
        if (emailInput && emailInput.reportValidity) emailInput.reportValidity();
        return;
      }
      subscribe(email.trim(), firstName.trim());
      mark('converted');
      if (capture) capture.hidden = true;
      if (success) {
        success.hidden = false;
        // Repoint the dialog label at the success heading and move focus to it.
        var heading = success.querySelector('.ohp-optin__success-heading');
        if (heading) {
          if (dialog && heading.id) dialog.setAttribute('aria-labelledby', heading.id);
          try { heading.focus({ preventScroll: true }); } catch (err) {}
        }
      }
    });
  }

  /* ---- tap to copy the code ---- */
  var copyBtn = root.querySelector('[data-ohp-optin-copy]');
  if (copyBtn) {
    copyBtn.addEventListener('click', function () {
      var hint = copyBtn.querySelector('[data-ohp-optin-copy-hint]');
      var done = function () { if (hint) { hint.textContent = 'Copied'; setTimeout(function () { hint.textContent = 'Tap to copy'; }, 1800); } };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(CODE).then(done).catch(function () { done(); });
      } else {
        done();
      }
    });
  }

  /* ---- arm the delay timer once, only if not already seen ---- */
  if (!seen()) {
    setTimeout(open, DELAY);
  }
})();
